import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import i18n from '../../i18n'
import { resetTableCheckbox } from '../../store/table'
import { invalidateQueries, updateToast } from '../helpers'
import {
    dismissNotifications,
    getAgentPreferences,
    getNotificationActiveTabs,
    getNotificationDeliveries,
    getTotalUnreadCount,
    restoreNotifications,
    toggleMarkAsRead,
    updateAgentPreferences,
} from './functions'
import {
    NotificationActiveTabs,
    NotificationDeliveriesParams,
    NotificationDeliveriesResponse,
    NotificationPreferencesResponse,
    NotificationPreferencesUpdateParams,
    NotificationPreferencesUpdateResponse,
    NotificationUnreadCountResponse,
    NotificationUpdateParams,
    NotificationUpdateResponse,
    RestoreNotificationsParams,
} from './types'

const notifications = {
    GetNotificationDeliveries: {
        useQuery: (
            params: NotificationDeliveriesParams,
            options?: Omit<UseQueryOptions<NotificationDeliveriesResponse, AxiosError>, 'queryKey' | 'queryFn'>
        ) =>
            useQuery({
                queryKey: ['notifications', 'deliveries', params],
                queryFn: () => getNotificationDeliveries(params),
                ...options,
            }),
    },
    GetTotalUnreadCount: {
        useQuery: (options?: Omit<UseQueryOptions<NotificationUnreadCountResponse, AxiosError>, 'queryKey' | 'queryFn'>) =>
            useQuery({
                queryKey: ['notifications', 'deliveries', 'unread-count'],
                queryFn: () => getTotalUnreadCount(),
                ...options,
            }),
    },
    DismissNotifications: {
        useMutation: (options?: UseMutationOptions<NotificationUpdateResponse, AxiosError, NotificationUpdateParams>) => {
            const dispatch = useDispatch()

            return useMutation({
                mutationFn: (params: NotificationUpdateParams) => dismissNotifications(params),
                onSuccess: () => {
                    invalidateQueries(['notifications', 'deliveries'])
                    dispatch(resetTableCheckbox(true))
                },
                onError: (error, _params, context) => {
                    const toastId = context as number
                    updateToast(toastId, i18n.t('toast.update.delete.error') as string, 'error')
                    console.error(error)
                },
                ...options,
            })
        },
    },
    RestoreNotifications: {
        useMutation: (options?: UseMutationOptions<NotificationUpdateResponse, AxiosError, RestoreNotificationsParams>) =>
            useMutation({
                mutationFn: (params: RestoreNotificationsParams) => restoreNotifications(params),
                onSuccess: () => {
                    invalidateQueries(['notifications', 'deliveries'])
                },
                onError: (error) => {
                    toast.error(i18n.t('app.common.toast.error') as string)
                    console.error(error)
                },
                ...options,
            }),
    },
    ToggleMarkAsRead: {
        useMutation: (options?: UseMutationOptions<NotificationUpdateResponse, AxiosError, NotificationUpdateParams>) => {
            return useMutation({
                mutationFn: (params: NotificationUpdateParams) => toggleMarkAsRead(params),
                onSuccess: () => {
                    invalidateQueries(['notifications', 'deliveries'])
                },
                onError: (error) => {
                    toast.error(i18n.t('app.common.toast.error') as string)
                    console.error(error)
                },
                ...options,
            })
        },
    },
    GetNotificationActiveTabs: {
        useQuery: (options?: Omit<UseQueryOptions<NotificationActiveTabs, AxiosError>, 'queryKey' | 'queryFn'>) =>
            useQuery({
                queryKey: ['notifications', 'active-tabs'],
                queryFn: () => getNotificationActiveTabs(),
                ...options,
            }),
    },
    GetAgentPreferences: {
        useQuery: (agentId: string, options?: Omit<UseQueryOptions<NotificationPreferencesResponse, AxiosError>, 'queryKey' | 'queryFn'>) =>
            useQuery({
                queryKey: ['notifications', 'preferences', agentId],
                queryFn: () => getAgentPreferences(agentId),
                ...options,
            }),
    },
    UpdateAgentPreferences: {
        useMutation: (
            agentId: string,
            options?: UseMutationOptions<NotificationPreferencesUpdateResponse, AxiosError, NotificationPreferencesUpdateParams>
        ) =>
            useMutation({
                mutationFn: (params: NotificationPreferencesUpdateParams) => updateAgentPreferences(agentId, params),
                onSuccess: () => {
                    invalidateQueries(['notifications', 'preferences', agentId])
                },
                onError: (error) => {
                    toast.error(i18n.t('app.common.toast.error') as string)
                    console.error(error)
                },
                ...options,
            }),
    },
}

export default notifications
